import { Button } from '@components/ui/button';
import type { LucideIcon } from 'lucide-react';
import { AlertCircle } from 'lucide-react';
import React from 'react';

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  iconClassName?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'outline' | 'ghost' | 'link' | 'destructive';
  };
  className?: string;
  /** Additional content to render between description and action button */
  children?: React.ReactNode;
}

/**
 * Reusable empty state component for consistent UI across the app
 * Matches the styling used in GenericTable empty states
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon: Icon = AlertCircle,
  iconClassName = 'text-muted-foreground',
  action,
  className = '',
  children,
}) => {
  return (
    <div className={`flex flex-col items-center justify-center h-full ${className}`}>
      <div className="text-center p-8 max-w-md">
        <Icon className={`w-16 h-16 mx-auto mb-4 ${iconClassName}`} />
        <h2 className="text-lg font-semibold text-foreground mb-2">{title}</h2>
        {description && <p className="text-sm text-muted-foreground mb-4">{description}</p>}
        {action && (
          <Button variant={action.variant || 'default'} onClick={action.onClick} className="mt-4">
            {action.label}
          </Button>
        )}
        {children}
      </div>
    </div>
  );
};

EmptyState.displayName = 'EmptyState';
